import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact voice-to-text pill. Records mic audio, sends it to `/api/voice/transcribe`
 * and hands the transcript back via `onAppend` so the caller decides whether
 * to append or replace. Kept intentionally small so it fits next to any label.
 */
export default function VoiceToTextButton({ onAppend, testId = "voice-to-text" }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const res = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (res.data?.transcript || "").trim();
          if (!text) {
            toast.error("No se detectó voz. Inténtalo de nuevo.");
            return;
          }
          onAppend?.(text);
          toast.success("Transcripción añadida");
        } catch (err) {
          console.error(err);
          toast.error("Error procesando el audio");
        } finally {
          setProcessing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  if (processing) {
    return (
      <span data-testid={`${testId}-processing`}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#FFC740]/25 text-[#0B2A4A] text-xs font-semibold">
        <Loader2 size={12} className="animate-spin" /> Transcribiendo…
      </span>
    );
  }

  if (recording) {
    return (
      <button type="button" onClick={stop} data-testid={`${testId}-stop`}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-red-500 text-white text-xs font-semibold voice-pulse">
        <Square size={12} fill="currentColor" /> Parar
      </button>
    );
  }

  return (
    <button type="button" onClick={start} data-testid={`${testId}-start`}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#0B2A4A] text-white text-xs font-semibold hover:bg-[#0a2340] active:scale-95 transition">
      <Mic size={12} /> Dictar
    </button>
  );
}
